import React, { useEffect, useMemo, useRef } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import { DialogButtons } from 'components/Dialog/components/DialogBody/components/DialogButtons';
import DialogDescription from 'components/Dialog/components/DialogDescription';
import styles from 'components/Dialog/style';
import { DialogProps } from 'components/Dialog/types';
import LocalizedText from 'components/LocalizedText';
import Text, { ETextCategory, ETextColor } from 'components/Text';

export default function DialogBody({
    style,
    title,
    titleNumberOfLines,
    titleStyle,
    description,
    DescriptionComponent,
    descriptionNumberOfLines,
    errorCode,
    actionButtonBordered,
    actionButtonTitle,
    actionButtonFillColor,
    actionButtonStyle,
    actionButtonTextStyle,
    actionButtonAccessibilityLabel,
    cancelButtonTitle,
    cancelButtonFillColor,
    cancelButtonBordered,
    cancelButtonStyle,
    cancelButtonTextStyle,
    cancelButtonAccessibilityLabel,
    handleCancelPress,
    handleActionButtonPress,
    revertButtons,
    titleTestID,
    descriptionTestID,
    actionButtonTestID,
    closeButtonTestID,
    errorCodeTestID,
    IconComponent,
    fromPlayer,
    forceTitleFocus = false,
}: Omit<
    DialogProps,
    'visible' | 'actionButtonOnPress' | 'cancelButtonOnPress'
> & {
    handleCancelPress?: () => void;
    handleActionButtonPress: () => void;
}) {
    const { bottom: bottomInset } = useDebouncedSafeAreaInsets();
    const { t } = useTranslation();
    const titleRef = useRef(null);

    const shouldShowErrorCode = useMemo(
        () =>
            errorCode &&
            ((typeof errorCode === 'number' && errorCode !== 1111) ||
                typeof errorCode === 'string'),
        [errorCode],
    );

    useEffect(() => {
        setTimeout(() => {
            setAccessibilityFocusTo(titleRef);
        }, 50);
    }, [forceTitleFocus]);

    return (
        <View
            style={[
                styles.container,
                isTabletOrWeb && styles.tabletContainer,
                style,
            ]}
        >
            <View style={styles.flex1}>
                <View style={styles.contentContainer}>
                    {IconComponent ? <IconComponent /> : null}
                    <Text
                        style={[styles.text, titleStyle]}
                        category={ETextCategory.title1}
                        color={ETextColor.neutral}
                        numberOfLines={titleNumberOfLines}
                        accessibilityRole="text"
                        ref={titleRef}
                        {...(isWeb && { tabIndex: 0 })}
                        accessible
                    >
                        <LocalizedText testID={titleTestID} textKey={title} />
                    </Text>
                    <DialogDescription
                        descriptionTestID={descriptionTestID}
                        description={description}
                        DescriptionComponent={DescriptionComponent}
                        descriptionNumberOfLines={descriptionNumberOfLines}
                    />
                    {shouldShowErrorCode && !isTabletOrWeb && (
                        <Text
                            category={ETextCategory.body1}
                            color={ETextColor.light_grey}
                            style={[styles.mobileErrorCode, styles.text]}
                            testID={errorCodeTestID}
                            accessibilityRole="text"
                            accessibilityLabel={`${t(
                                LANGUAGE_KEYS.ERROR_CODE,
                            )} ${errorCode}`}
                            accessible
                        >
                            <LocalizedText textKey={LANGUAGE_KEYS.ERROR_CODE} />{' '}
                            {errorCode}
                        </Text>
                    )}
                    <View
                        style={
                            isTabletOrWeb || fromPlayer
                                ? styles.largeScreenButtons
                                : [
                                      styles.phoneButtons,
                                      {
                                          bottom: Math.max(24, bottomInset),
                                      },
                                  ]
                        }
                    >
                        <DialogButtons
                            actionButtonTestID={actionButtonTestID}
                            closeButtonTestID={closeButtonTestID}
                            handleActionButtonPress={handleActionButtonPress}
                            actionButtonBordered={actionButtonBordered}
                            actionButtonTitle={actionButtonTitle}
                            actionButtonFillColor={actionButtonFillColor}
                            actionButtonStyle={actionButtonStyle}
                            actionButtonTextStyle={actionButtonTextStyle}
                            actionButtonAccessibilityLabel={
                                actionButtonAccessibilityLabel
                            }
                            handleCancelPress={handleCancelPress}
                            cancelButtonTitle={cancelButtonTitle}
                            cancelButtonFillColor={cancelButtonFillColor}
                            cancelButtonBordered={cancelButtonBordered}
                            cancelButtonStyle={cancelButtonStyle}
                            cancelButtonTextStyle={cancelButtonTextStyle}
                            cancelButtonAccessibilityLabel={
                                cancelButtonAccessibilityLabel
                            }
                            revertButtons={revertButtons}
                        />
                    </View>
                </View>
            </View>

            {shouldShowErrorCode && isTabletOrWeb && (
                <Text
                    category={ETextCategory.body2}
                    color={ETextColor.light_grey}
                    style={styles.tabletError}
                    accessibilityRole="text"
                    accessibilityLabel={`${t(
                        LANGUAGE_KEYS.ERROR_CODE,
                    )} ${errorCode}`}
                    {...(isWeb && { tabIndex: 0 })}
                    accessible
                >
                    <LocalizedText textKey={LANGUAGE_KEYS.ERROR_CODE} />{' '}
                    {errorCode}
                </Text>
            )}
        </View>
    );
}
